import React, { useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
} from '@material-ui/core';
import { ArrowBack as BackIcon } from '@material-ui/icons';
import { useStyles } from './style';
import TestLogViewer from './TestLogViewer';

interface TestRunDetailProps {
  run: any;
  onBack: () => void;
  onCleanup: (programId: string, runId: string) => void;
  cleaningUp: boolean;
}

const TestRunDetail: React.FC<TestRunDetailProps> = ({ run, onBack, onCleanup, cleaningUp }) => {
  const classes = useStyles();

  if (!run) return null;

  const getStatusClass = (status: string) => {
    const map: Record<string, string> = {
      pending: classes.statusPending,
      running: classes.statusRunning,
      success: classes.statusSuccess,
      error: classes.statusError,
      cleaning: classes.statusCleaning,
      cleaned: classes.statusCleaned,
    };
    return map[status] || '';
  };

  return (
    <Box>
      <Button
        startIcon={<BackIcon />}
        onClick={onBack}
        className={classes.backButton}
      >
        Back to List
      </Button>

      <Paper className={classes.card} style={{ padding: 24 }}>
        <Grid container spacing={3} className={classes.detailGrid}>
          <Grid item xs={12}>
            <Typography variant="h6" style={{ fontWeight: 700 }}>
              {run.runId}
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Status</Typography>
            <Box mt={0.5}>
              <Chip
                label={run.status}
                size="small"
                className={`${classes.statusChip} ${getStatusClass(run.status)}`}
              />
            </Box>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Test ID</Typography>
            <Typography>{run.testId}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Prefix</Typography>
            <Typography style={{ fontFamily: 'monospace' }}>{run.prefix}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Program ID</Typography>
            <Typography style={{ fontFamily: 'monospace', fontSize: 12 }}>
              {run.programId || '-'}
            </Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Started</Typography>
            <Typography>{run.startedAt ? new Date(run.startedAt).toLocaleString() : '-'}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Completed</Typography>
            <Typography>{run.completedAt ? new Date(run.completedAt).toLocaleString() : '-'}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Users Created</Typography>
            <Typography>{run.createdUserIds?.length || 0}</Typography>
          </Grid>

          <Grid item xs={3}>
            <Typography variant="caption" color="textSecondary">Enrollments</Typography>
            <Typography>{run.createdEnrollmentIds?.length || 0}</Typography>
          </Grid>
        </Grid>

        {run.programId && run.status !== 'cleaned' && (
          <Box mt={2}>
            <Button
              variant="outlined"
              color="secondary"
              className={classes.actionButton}
              onClick={() => onCleanup(run.programId, run.runId)}
              disabled={cleaningUp}
            >
              {cleaningUp ? 'Cleaning up...' : 'Cleanup Test Data'}
            </Button>
          </Box>
        )}
      </Paper>

      {/* Errors */}
      {run.errors?.length > 0 && (
        <Paper className={classes.card} style={{ padding: 24 }}>
          <Typography variant="h6" style={{ fontWeight: 700, color: '#991B1B', marginBottom: 16 }}>
            Errors ({run.errors.length})
          </Typography>
          {run.errors.map((err: any, i: number) => (
            <Box key={i} mb={2} p={2} style={{ backgroundColor: '#FEF2F2', borderRadius: 8 }}>
              <Typography style={{ fontWeight: 600 }}>{err.action}</Typography>
              <Typography style={{ color: '#991B1B' }}>{err.error}</Typography>
              {err.stack && (
                <Typography
                  variant="body2"
                  style={{ fontFamily: 'monospace', fontSize: 11, color: '#6B7280', marginTop: 4 }}
                >
                  {err.stack}
                </Typography>
              )}
            </Box>
          ))}
        </Paper>
      )}

      {/* Logs */}
      <Paper className={classes.card} style={{ padding: 24 }}>
        <Typography variant="h6" style={{ fontWeight: 700, marginBottom: 16 }}>
          Logs ({run.logs?.length || 0})
        </Typography>
        <TestLogViewer logs={run.logs || []} />
      </Paper>
    </Box>
  );
};

export default TestRunDetail;
